import React, {
  ChangeEvent,
  FC,
  KeyboardEvent,
  useEffect,
  useState,
} from "react";
import { Box } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import { FormattedMessage } from "react-intl";
import { Mail, Lock } from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import styles from "./style";
import { useToastNotifications } from "../../utils/toastUtils";
import Layout from "../Common/NewLayout";
import Header from "../Common/NewLayout/Header";
import CustomTextField from "../Ui/CustomTextField";
import { CustomButton } from "../Ui/CustomButton";
import { isEmailValid, isValidPassword } from "../../utils/Validator";
import { isAuthenticated, setAccessToken } from "../../services/LocalStorage";
import PasswordMeter from "../Common/PasswordMeter/PasswordMeter";
import { DASHBOARD_URL } from "../../config";
import {
  CreateAccount as CreateAccountMutation,
  CreateAccountVariables,
} from "../../graphql/account/types/CreateAccount";
import { CREATE_ACCOUNT } from "../../graphql/account/mutation";
import { SapheerError } from "../../enums/error";
import { checkValidNumber, formatPhoneNumberIntl } from "../../utils/phone";
import { removeAllSpace } from "../../utils/char";
import ValidationCode from "./ValidationCode";
import CustomPhone from "../Ui/CustomPhoneField";

interface IAccountState {
  phone_number: string;
  email: string;
  password: string;
  passwordConfirm: string;
}
const CreateAccount: FC = () => {
  const classes = styles();
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const initAccountState: IAccountState = {
    phone_number: "",
    email: "",
    password: "",
    passwordConfirm: "",
  };
  const [account, setAccount] = useState(initAccountState);
  const [open, setOpen] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const { showErrorToast, showSuccessToast } = useToastNotifications();
  const [createAccount, { loading: accountLoading }] = useMutation<
    CreateAccountMutation,
    CreateAccountVariables
  >(CREATE_ACCOUNT, {
    onCompleted: async (data) => {
      const { message, success, accessToken } = data?.createAccount || {};
      if (message && !success) {
        showErrorToast({
          description: "stepone.authentication.failed",
        });
      } else {
        if (accessToken) {
          await setAccessToken(accessToken);
          navigate("/step", {
            replace: true,
          });
        } else {
          showSuccessToast({
            description: "account.code.send",
          });
          handleOpen();
        }
      }
    },
    onError: (error) => {
      const { graphQLErrors, networkError } = error;
      const { message } = graphQLErrors?.[0] || {};
      let msg = "other.error";
      if (message) {
        switch (true) {
          case message.includes(SapheerError.CORPORATE_ALREADY_VALIDATED):
            msg = "COMPANY_ALREADY_VALIDATED";
            break;
          case message.includes(SapheerError.INVALID_PASSWORD):
            msg = "stepone.authentication.password.failed";
            break;
          case message.includes(SapheerError.PHONE_ALREADY_EXIST):
            msg = "member.phone.already.exists";
            break;
        }
      } else if (networkError) {
        msg = "network.error";
      }
      showErrorToast({
        description: msg,
      });
    },
  });

  useEffect(() => {
    isAuthenticated().then((auth) => {
      if (auth) {
        navigate("/step", { replace: true });
      }
    });
  }, [navigate]);

  const handleSubmit = () => {
    setIsSubmitted(true);
    const { email, password, phone_number: phone } = account;
    const phone_number = removeAllSpace(phone);
    // Validation
    if (!email && !password) {
      return showErrorToast({
        description: "account.empty",
      });
    }
    if (!email) {
      return showErrorToast({
        description: "account.email.empty",
      });
    }
    if (!phone_number) {
      return showErrorToast({
        description: "phone.empty",
      });
    }
    if (!checkValidNumber(phone_number)) {
      return showErrorToast({
        description: "Votre numéro de téléphone n'est pas valide",
      });
    }
    if (!password) {
      return showErrorToast({
        description: "account.password.empty",
      });
    }
    if (!isEmailValid(email)) {
      return showErrorToast({
        description: "account.data.email.notValid",
      });
    }
    if (!isValidPassword(password)) {
      return showErrorToast({
        description: "account.data.password.notValid",
      });
    }
    if (!validatePasswordConfirm()) {
      return showErrorToast({
        description: "account.password.notMatch",
      });
    }

    createAccount({
      variables: {
        email: email.toLowerCase(),
        password,
        phone: "+" + phone_number,
      },
    });
  };
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const validatePasswordConfirm = () => {
    const { password, passwordConfirm } = account;
    return password === passwordConfirm;
  };
  const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAccount({ ...account, [name]: value });
    if (name === "password") {
      if (isValidPassword(value)) {
        setIsValid(true);
      } else {
        setIsValid(false);
      }
    }
  };

  const handlePhoneChange = (name: string, value: string) => {
    setAccount({ ...account, [name]: value });
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleLogin = () => {
    window.location.href = `${DASHBOARD_URL}/login`;
  };

  return (
    <>
      <Layout
        title="Ouvrez votre compte en quelques minutes"
        description="Gérez vos comptes, suivez vos transactions et effectuez vos
              opérations en toute sécurité."
      >
        <Header
          title="Créer votre compte"
          description="Entrez votre e-mail afin de recevoir le code de validation."
        />
        <Box marginTop={2} onKeyPress={handleKeyPress}>
          <CustomTextField
            name="email"
            label={<FormattedMessage id="form.email" />}
            placeholder="exemple@email.com"
            onChange={handleValueChange}
            errorMessageId="account.email.empty"
            value={account.email}
            iconLeft={Mail}
            isSubmitted={isSubmitted}
          />
          <CustomPhone
            name="phone_number"
            label="Téléphone"
            onChange={handlePhoneChange}
            errorMessageId="phone.empty"
            value={formatPhoneNumberIntl(account?.phone_number)}
            isSubmitted={isSubmitted}
          />

          {!isValid && (
            <Box marginBottom={1}>
              <Typography
                className={classes.passwordInfo}
                variant="body2"
                color="primary"
              >
                <FormattedMessage id="form.password.contentError" />
              </Typography>
            </Box>
          )}
          <CustomTextField
            name="password"
            label={<FormattedMessage id="form.password" />}
            onChange={handleValueChange}
            errorMessageId="account.password.empty"
            value={account.password}
            iconLeft={Lock}
            isSubmitted={isSubmitted}
            type="password"
          />
          <PasswordMeter password={account.password} />
          <Box marginBottom={1}></Box>
          <CustomTextField
            name="passwordConfirm"
            label={<FormattedMessage id="form.confirmPassword" />}
            onChange={handleValueChange}
            errorMessageId={
              !validatePasswordConfirm()
                ? "account.password.notMatch"
                : "account.password.empty"
            }
            value={account.passwordConfirm}
            iconLeft={Lock}
            isSubmitted={isSubmitted}
            type="password"
          />
          <CustomButton
            type="button"
            variant="primary"
            onClick={handleSubmit}
            fullWidth
            label={"button.next"}
            loading={accountLoading}
          />
        </Box>
        <Typography className={classes.text} align="center">
          Si vous avez déjà un compte, vous pouvez vous connecter en{" "}
          <Link className={classes.link} onClick={handleLogin}>
            cliquant ici
          </Link>
          .{" "}
        </Typography>
        <ValidationCode
          handleClose={handleClose}
          open={open}
          email={account.email}
        />
      </Layout>
    </>
  );
};
export default CreateAccount;
